import { flushTelemetry, track } from './telemetry';

/**
 * How the game runs, on the device it is actually running on.
 *
 * The one question this exists to answer: **is the game slow for anyone?**
 * Nothing here says whose device it was. GDD §12's never-list rules out the
 * device fingerprint, the screen size and the user agent, so a slow session
 * is visible as a slow session and never as a slow phone — the distribution
 * carries the finding, and the tail is the population worth fixing for.
 *
 * Two rules this module exists to keep:
 *
 * - **Measuring may not cost a frame.** Every signal here is either read
 *   from a passive `PerformanceObserver` the browser fills in anyway, or
 *   taken from a `performance.now()` pair around work the game was already
 *   doing. There is deliberately no sampling loop: a `requestAnimationFrame`
 *   that runs in order to time frames is itself a reason the page can never
 *   go idle, which is the exact fault this was written to find.
 * - **Durations only.** Same rule as the rest of the stream — the server
 *   owns the epoch, and nothing here reads a wall clock.
 *
 * Everything is one-sided in the §12 sense. `longtask` and `event` timing
 * are Chromium-shaped APIs; a browser without them reports the fields it can
 * and leaves the rest absent, which reads as "not measured here", never as
 * zero.
 */

/** A task over this many ms is long enough that a person feels it. */
const LONG_TASK_MS = 50;

/**
 * How small an interaction has to be before it is not worth a row.
 *
 * 16ms is one frame at 60Hz: below it the tap landed within the frame it
 * arrived in, and there is nothing to report.
 */
const EVENT_THRESHOLD_MS = 16;

/** Renders kept for the percentile. Beyond this the oldest are dropped. */
const MAX_RENDERS = 200;

let armed = false;
let sent = false;
let bootMs: number | null = null;

let longTasks = 0;
let blockedMs = 0;
let interactions = 0;
let worstInteractionMs = 0;
const renders: number[] = [];

const observers: PerformanceObserver[] = [];

/**
 * `PerformanceObserverInit` as the browsers actually take it.
 *
 * `durationThreshold` is part of Event Timing and is what keeps the observer
 * from being handed every trivial tap; the DOM lib does not declare it yet.
 */
interface ObserveInit {
  durationThreshold?: number;
}

/**
 * Watch a stream, and shrug if this browser does not have it.
 *
 * An unsupported entry type throws on `observe`, and a browser that reports
 * nothing is a browser this reading is absent for — not a failure worth
 * surfacing on the path of play.
 */
function observe(type: string, init: ObserveInit, on: (e: PerformanceEntry) => void) {
  try {
    const o = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) on(entry);
    });
    o.observe({ type, buffered: true, ...init });
    observers.push(o);
  } catch {
    /* this browser does not report this stream; the field stays absent */
  }
}

/** Arm the passive observers. Safe to call once; later calls do nothing. */
export function initPerf(): void {
  if (armed) return;
  armed = true;

  // Main-thread blocking. The count says how often the game stopped
  // answering; the total says for how long altogether.
  observe('longtask', {}, (e) => {
    longTasks++;
    blockedMs += Math.max(0, e.duration - LONG_TASK_MS);
  });

  // Interaction latency — tap to the frame that shows the answer. The worst
  // one in a session is the one a player would describe as "slow", so that
  // is the one kept rather than an average that hides it.
  observe('event', { durationThreshold: EVENT_THRESHOLD_MS }, (e) => {
    interactions++;
    worstInteractionMs = Math.max(worstInteractionMs, e.duration);
  });

  // A session that never finished a grid is exactly the session a slow
  // device would have caused, so the reading has to survive the way out as
  // well. The transport's own lifecycle listener was armed first, in
  // main.ts, and therefore runs before this one and posts without seeing
  // the row this adds — so the flush is explicit, for the same reason
  // `grid_end` flushes explicitly in app.ts.
  const doc = globalThis.document;
  const leave = () => {
    flushPerf();
    flushTelemetry('pagehide');
  };
  doc?.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') leave();
  });
  globalThis.window?.addEventListener('pagehide', leave);
}

/**
 * How long the game took to become playable.
 *
 * Called once, when the first board is on screen. Measured from the
 * navigation itself rather than from script start, so it includes the wait
 * for the bundle — which on a slow connection is most of it.
 */
export function noteBooted(): void {
  if (bootMs !== null) return;
  bootMs = Math.max(0, Math.round(globalThis.performance?.now?.() ?? 0));
}

/** Record one board render's cost. Called around `BoardRenderer.render()`. */
export function noteRender(ms: number): void {
  renders.push(ms);
  if (renders.length > MAX_RENDERS) renders.shift();
}

/** The p-th percentile of a sample, to a tenth, or `undefined` if empty. */
function percentile(xs: number[], p: number): number | undefined {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const i = Math.min(s.length - 1, Math.floor((p / 100) * s.length));
  return Math.round(s[i] * 10) / 10;
}

/**
 * Send the session's reading, once.
 *
 * Called when a grid ends and again on the way out, because a session that
 * never finished a grid is exactly the session a performance problem would
 * have caused. The `sent` latch is what keeps that from being two rows.
 */
export function flushPerf(): void {
  if (sent || !armed) return;
  sent = true;

  track('perf', {
    bootMs: bootMs ?? undefined,
    inpMs: interactions ? Math.round(worstInteractionMs) : undefined,
    interactions: interactions || undefined,
    longTasks: longTasks || undefined,
    blockedMs: longTasks ? Math.round(blockedMs) : undefined,
    rendersSampled: renders.length || undefined,
    renderP50Ms: percentile(renders, 50),
    renderP95Ms: percentile(renders, 95),
  });
}

/** Disarm completely. Tests use this; the game never does. */
export function resetPerf(): void {
  for (const o of observers) o.disconnect();
  observers.length = 0;
  armed = false;
  sent = false;
  bootMs = null;
  longTasks = 0;
  blockedMs = 0;
  interactions = 0;
  worstInteractionMs = 0;
  renders.length = 0;
}
